import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Card, Table, Button, Space, Spin, Empty, message, Typography } from 'antd';
import { FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useStore } from '../../../stores/root-store-context';
import { CrmReportRequest, CrmReportResult, CrmReportRow } from '../../../api/CRMApiClient';
import { chartToPngBase64 } from '../../../helpers/chartExport';
import { downloadBlob } from '../../../helpers/downloadBlob';
import { CHART_COLORS, ReportChartType } from './reportPresets';

const { Text } = Typography;

interface Props {
  title: string;
  request: CrmReportRequest;
  chart: ReportChartType;
}

const ReportCard: React.FC<Props> = observer(({ title, request, chart }) => {
  const { t, i18n } = useTranslation();
  const { crmStore } = useStore();
  const [result, setResult] = useState<CrmReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const requestKey = JSON.stringify(request);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    crmStore.aggregateCrmReport(request).then(r => {
      if (!cancelled) setResult(r);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  const labelOf = (label?: string) => (label ? label : t('NotSpecified'));

  const hasSecondary = !!request.secondaryGroupBy;

  /** Rows pivoted for a stacked bar: one object per primary key, one numeric field per secondary label. */
  const { pivot, series } = useMemo(() => {
    const rows = result?.rows ?? [];
    if (!hasSecondary) return { pivot: rows.map(r => ({ name: labelOf(r.label), count: r.count })), series: ['count'] };
    const seriesSet = new Set<string>();
    const byPrimary = new Map<string, Record<string, number | string>>();
    rows.forEach(r => {
      const s = labelOf(r.secondaryLabel);
      seriesSet.add(s);
      const entry = byPrimary.get(r.key) ?? { name: labelOf(r.label) };
      entry[s] = ((entry[s] as number) || 0) + r.count;
      byPrimary.set(r.key, entry);
    });
    return { pivot: Array.from(byPrimary.values()), series: Array.from(seriesSet) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, hasSecondary, i18n.language]);

  const doExport = async (format: 'xlsx' | 'pdf') => {
    setExporting(format);
    try {
      let chartImageBase64: string | undefined;
      if (format === 'pdf') {
        try {
          chartImageBase64 = await chartToPngBase64(chartRef.current);
        } catch {
          chartImageBase64 = undefined; // a chart rasterization failure must not lose the export
        }
      }
      const file = await crmStore.exportCrmReport(
        { ...request, language: i18n.language?.slice(0, 2), title, chartImageBase64 },
        format,
      );
      if (!file) { message.error(t('ExportFailed')); return; }
      downloadBlob(file.blob, file.fileName);
    } catch {
      message.error(t('ExportFailed'));
    } finally {
      setExporting(null);
    }
  };

  const columns = [
    { title: t(request.groupBy), dataIndex: 'label', key: 'label', render: (l: string) => labelOf(l) },
    ...(hasSecondary ? [{ title: t(request.secondaryGroupBy!), dataIndex: 'secondaryLabel', key: 'secondary', render: (l?: string) => labelOf(l) }] : []),
    { title: t('Count'), dataIndex: 'count', key: 'count', width: 100, align: 'right' as const },
  ];

  const renderChart = () => {
    if (!pivot.length) return <Empty description={t('NoDataForPeriod')} />;
    if (chart === 'pie' && !hasSecondary) {
      return (
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={pivot} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
              {pivot.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    return (
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={pivot} margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" angle={-25} textAnchor="end" height={60} tick={{ fontSize: 11 }} interval={0} />
          <YAxis allowDecimals={false} />
          <Tooltip />
          {hasSecondary && <Legend />}
          {series.map((s, i) => (
            <Bar key={s} dataKey={s} name={hasSecondary ? s : t('Count')} stackId={chart === 'stackedBar' ? 'a' : undefined} fill={CHART_COLORS[i % CHART_COLORS.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Card
      bordered={false}
      className="crm-report-card"
      title={<span>{title} {result ? <Text type="secondary" style={{ fontWeight: 400 }}>· {t('Total')}: {result.total}</Text> : null}</span>}
      extra={
        <Space>
          <Button icon={<FileExcelOutlined />} loading={exporting === 'xlsx'} onClick={() => doExport('xlsx')}>Excel</Button>
          <Button icon={<FilePdfOutlined />} loading={exporting === 'pdf'} onClick={() => doExport('pdf')}>PDF</Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <div ref={chartRef}>{renderChart()}</div>
        <Table
          className="crm-table"
          style={{ marginTop: 12 }}
          size="small"
          dataSource={result?.rows ?? []}
          columns={columns}
          rowKey={(r: CrmReportRow) => `${r.key}|${r.secondaryKey ?? ''}`}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
        />
      </Spin>
    </Card>
  );
});

export default ReportCard;
